import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, Wrench, UserPlus, PlusCircle } from "lucide-react";
import { getBranchInventory, getSparePartTypes } from "@/integrations/supabase/inventory-services";
import { useAuth } from "@/context/AuthContext";
import AssignToBikeModal from "./AssignToBikeModal";
import AssignToOperatorModal from "./AssignToOperatorModal";
import AddToBranchInventoryModal from "./AddToBranchInventoryModal";
import { BranchInventoryWithRelations, SparePartType } from "@/types/inventory";

const BranchInventoryView = () => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [showAssignToBikeModal, setShowAssignToBikeModal] = useState(false);
  const [showAssignToOperatorModal, setShowAssignToOperatorModal] = useState(false);
  const [showAddToBranchInventoryModal, setShowAddToBranchInventoryModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BranchInventoryWithRelations | null>(null);

  const isOperator = profile?.role === 'operator';
  const isBranchAdmin = profile?.role === 'branch_admin';
  const isSuperAdmin = profile?.role === 'super_admin';

  // For operators and branch admins, scope the query to their branch.
  // For super admins, branchId is undefined, so it fetches all.
  const branchId = isOperator || isBranchAdmin ? profile?.branch_id : undefined;

  const { data: branchInventory, isLoading, isError, error } = useQuery<BranchInventoryWithRelations[]>({
    queryKey: ['branch-inventory', branchId],
    queryFn: () => getBranchInventory(branchId),
    enabled: !!profile, // Only run query if profile is loaded
  });

  // Fetch spare part types for the Add Modal (only if branch admin)
  const { data: sparePartTypes } = useQuery<SparePartType[]>({
    queryKey: ['spare-part-types'],
    queryFn: getSparePartTypes,
    enabled: isBranchAdmin,
  });

  const handleAssignToBike = (item: BranchInventoryWithRelations) => {
    setSelectedItem(item);
    setShowAssignToBikeModal(true);
  };
  
  const handleAssignToOperator = (item: BranchInventoryWithRelations) => {
    setSelectedItem(item);
    setShowAssignToOperatorModal(true);
  };

  const handleAddToBranchInventory = () => {
    setShowAddToBranchInventoryModal(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['branch-inventory'] });
  };

  if (isLoading) {
    return <div className="text-center py-4">Cargando inventario...</div>;
  }
  
  if (isError) {
    return <div className="text-center py-4 text-red-500">Error al cargar el inventario: {error.message}</div>;
  }

  // Super admin sees everything, other roles see their branch inventory by default due to query scoping
  const inventoryToShow = branchInventory;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          {isSuperAdmin ? 'Inventario de Todas las Sucursales' : `Inventario de Sucursal: ${profile?.branch?.name || ''}`}
        </h3>
        {isBranchAdmin && (
           <Button onClick={handleAddToBranchInventory} className="flex items-center gap-2">
             <PlusCircle className="h-4 w-4" />
             Añadir Repuesto
           </Button>
        )}
      </div>

      <div className="grid gap-4">
        {inventoryToShow?.map((item) => (
          <Card key={item.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-bike-600 mt-1" />
                  <div>
                    <CardTitle className="text-lg">{item.spare_part_type?.name}</CardTitle>
                    {item.spare_part_type?.description && (
                      <p className="text-sm text-gray-600">{item.spare_part_type.description}</p>
                    )}
                    {isSuperAdmin && (
                      <p className="text-sm text-gray-500">Sucursal: {item.branch?.name}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {isOperator && profile.branch_id === item.branch_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAssignToBike(item)}
                      className="flex items-center gap-2"
                      disabled={item.quantity === 0}
                    >
                      <Wrench className="h-4 w-4" />
                      Asignar a Bicicleta
                    </Button>
                  )}
                  {isBranchAdmin && (
                     <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAssignToOperator(item)}
                        className="flex items-center gap-2"
                      >
                        <UserPlus className="h-4 w-4" />
                        {item.assigned_to ? 'Reasignar' : 'Asignar'}
                      </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">Cantidad disponible:</span>
                  <span className={`text-lg font-bold ${item.quantity === 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {item.quantity} unidades
                  </span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">Asignado a: </span>
                  {item.assigned_to_profile ? (
                    <span className="font-semibold text-bike-700">{item.assigned_to_profile.full_name}</span>
                  ) : (
                    <span className="text-gray-500 italic">Sin asignar</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {inventoryToShow?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No hay repuestos en el inventario de esta sucursal.
          </div>
        )}
      </div>

      <AssignToBikeModal
        isOpen={showAssignToBikeModal}
        onClose={() => {
          setShowAssignToBikeModal(false);
          setSelectedItem(null);
        }}
        inventoryItem={selectedItem}
        onSuccess={handleSuccess}
      />

      <AssignToOperatorModal
        isOpen={showAssignToOperatorModal}
        onClose={() => {
          setShowAssignToOperatorModal(false);
          setSelectedItem(null);
        }}
        inventoryItem={selectedItem}
        onSuccess={handleSuccess}
      />

      {isBranchAdmin && branchId && (
        <AddToBranchInventoryModal
          isOpen={showAddToBranchInventoryModal}
          onClose={() => setShowAddToBranchInventoryModal(false)}
          sparePartTypes={sparePartTypes || []}
          branchId={branchId}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default BranchInventoryView;